/**
 * The authenticated application shell + route gate.
 *
 * `<RequireSession>` enforces a live session — bouncing to the external login
 * site on a miss and showing the loop-guard fallback if a redirect loop trips.
 * Inside it, {@link SessionScopedProviders} supplies flags/tenant/permissions
 * from the session claims, then the sidebar + header frame the routed `Outlet`.
 *
 * `<DebugResourceProvider>` sits between the routed content and `<DebugPanel>`
 * so a page can publish diagnostics about the RECORD it is showing (`?debug=1`)
 * and the panel grows a tab for it. Most resources need no page code at all —
 * the API client captures whatever the backend published — so the provider is
 * given the pathname to clear those captures on navigation, which is what stops
 * the previous page's record lingering in the panel. Mounted unconditionally
 * and inert with debug off; the shell never learns which routes publish.
 */
import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { Button } from "@authe/ui-core";
import { RequireSession, useSession } from "@authe/ui-core/session";
import { DebugResourceProvider } from "@authe/ui-core/debug";
import { AskAiProvider } from "./ask-ai-shell";
import { DisplayPrefsIdentity, SessionScopedProviders } from "./providers";
import { DocumentTitleProvider } from "./documentTitle";
import { PostSetupTokenGuard } from "./PostSetupTokenGuard";
import { Sidebar } from "@/components/shell/Sidebar";
import { Header } from "@/components/shell/Header";
import { DebugPanel } from "@/components/DebugPanel";
import { APP_NAME, DASHBOARD_ERRORS } from "@/config";
import { MAIN_CONTENT_ID } from "./layoutIds";

function SigningIn(): React.ReactElement {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      <p className="text-sm text-muted-foreground">Signing in to {APP_NAME}…</p>
    </div>
  );
}

function AuthNotice({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex h-screen items-center justify-center bg-background p-6">
      <div className="flex max-w-sm flex-col items-center text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        {action}
      </div>
    </div>
  );
}

/**
 * The circuit breaker tripped — sign-in really is bouncing. No retry button:
 * another attempt would just extend the loop.
 */
function AuthBlocked(): React.ReactElement {
  return <AuthNotice {...DASHBOARD_ERRORS.SESSION_LOOP} />;
}

/**
 * A terminal auth failure, which today means a *refused* sign-in — the
 * post-login Action denying (e.g. a removed org). An expired/stale session is
 * not terminal: the session manager clears the dead token cache and the guard
 * below redirects to login, so it never reaches this screen. Auth0's own deny
 * reason is the useful copy when we have it.
 */
function AuthError(): React.ReactElement {
  const { state, login } = useSession();
  return (
    <AuthNotice
      title={DASHBOARD_ERRORS.SESSION_ENDED.title}
      description={state.error?.message || DASHBOARD_ERRORS.SESSION_ENDED.description}
      action={
        <Button size="sm" className="mt-4" onClick={() => void login()}>
          Sign in again
        </Button>
      }
    />
  );
}

/**
 * Start a new page at the top.
 *
 * The shell scrolls in `<main>`, not the window, so neither the browser nor the
 * router's own scroll restoration (which targets the window) does anything here
 * — navigate from halfway down a transaction to another one and you land
 * halfway down that one, or wherever the shorter page clamps to.
 *
 * Keyed on the PATH only. A tab or chain selector that rewrites just the search
 * params (`replace: true`) is the same page, and yanking the user to the top
 * when they pick a tab is its own bug.
 */
function useScrollToTopOnRouteChange(ref: React.RefObject<HTMLElement | null>): void {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  React.useEffect(() => {
    ref.current?.scrollTo({ top: 0 });
  }, [pathname, ref]);
}

export function RootLayout(): React.ReactElement {
  const mainRef = React.useRef<HTMLElement>(null);
  useScrollToTopOnRouteChange(mainRef);
  // Path only, for the same reason the scroll reset uses it: switching a tab or
  // a chain selector rewrites the search params on the SAME record, and
  // dropping the debug captures there would blank the panel mid-inspection.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The phone-width navigation drawer (the rail itself is hidden below 768px).
  const [navOpen, setNavOpen] = React.useState(false);
  const openNav = React.useCallback(() => setNavOpen(true), []);
  return (
    <RequireSession loading={<SigningIn />} blocked={<AuthBlocked />} error={<AuthError />}>
      {/*
        Self-heal a stale/bootstrap-only token from the setup hand-off before the
        app shell paints: one hard refresh, else bounce to /setup. Sits ABOVE the
        session-scoped layers so we never render the dashboard with setup-only
        scopes. See PostSetupTokenGuard.
      */}
      <PostSetupTokenGuard>
        <SessionScopedProviders>
          {/* Behaviours, not layout: the first binds the preference keyspace to
              the signed-in analyst, the second keeps the tab's name in step with
              the route and whatever the page is about. */}
          <DisplayPrefsIdentity />
          <DocumentTitleProvider>
          <AskAiProvider>
            <DebugResourceProvider routeKey={pathname}>
              <div className="flex h-screen overflow-hidden bg-background">
                <Sidebar mobileOpen={navOpen} onMobileOpenChange={setNavOpen} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <Header onOpenNav={openNav} />
                  {/* `id` is the skip link's target: the sidebar is 26 entries
                      deep, so reaching the page by keyboard without one means
                      tabbing all of them on every navigation. */}
                  <main id={MAIN_CONTENT_ID} ref={mainRef} className="flex-1 overflow-y-auto p-4 md:p-6">
                    <Outlet />
                  </main>
                </div>
              </div>
              <DebugPanel />
            </DebugResourceProvider>
          </AskAiProvider>
          </DocumentTitleProvider>
        </SessionScopedProviders>
      </PostSetupTokenGuard>
    </RequireSession>
  );
}
